import React, { createContext, useContext, useState, ReactNode } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { useLocation } from "../contexts/LocationContext";
import axios from "axios";
import {
  CheckIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  ClockIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";
import PlaceAutocomplete from "../components/PlaceAutocomplete";
import { Location } from "@visitproof/types";
import { useCase } from "../contexts/CaseContext";
import { Text } from "../design/text";

interface Errors {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  saveError?: string;
}

interface LocationDialogContextType {
  openCreateDialog: (options: { caseId: string; onLocationCreated?: (locationId: string) => void }) => void;
  openEditDialog: (options: { location: Location }) => void;
}

const LocationDialogContext = createContext<
  LocationDialogContextType | undefined
>(undefined);

export const useLocationDialog = () => {
  const context = useContext(LocationDialogContext);
  if (!context) {
    throw new Error(
      "useLocationDialog must be used within a LocationDialogProvider",
    );
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const LocationDialogProvider: React.FC<Props> = ({ children }) => {
  const { createLocation, updateLocation, locations } = useLocation();
  const { setSelectedCase } = useCase();
  
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [caseId, setCaseId] = useState<string>("");
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [onLocationCreatedCallback, setOnLocationCreatedCallback] = useState<((locationId: string) => void) | null>(null);
  
  // Form state
  const [street, setStreet] = useState<string>("");
  const [street2, setStreet2] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [googleResults, setGoogleResults] = useState<any[]>([]);
  const [visualState, setVisualState] = useState<"google" | "manual" | "past">(
    "google",
  );

  const openCreateDialog = (options: { caseId: string; onLocationCreated?: (locationId: string) => void }) => {
    setMode("create");
    setCaseId(options.caseId);
    setEditingLocation(null);
    // Store callback as a function that returns the callback to avoid React treating it as a state updater
    setOnLocationCreatedCallback(() => options.onLocationCreated || null);
    clearForm();
    setIsOpen(true);
  };

  const openEditDialog = (options: { location: Location }) => {
    setMode("edit");
    setEditingLocation(options.location);
    setName(options.location.name);
    setCaseId(options.location.caseId);
    clearForm();
    setIsOpen(true);
  };

  const validateAddressWithGoogle = async () => {
    // Construct the address string
    let address = ``;
    if (street) {
      address += `${street}, `;
    }
    if (street2) {
      address += `${street2}, `;
    }
    if (city) {
      address += `${city}, `;
    }
    if (state) {
      address += `${state}, `;
    }
    if (zip) {
      address += `${zip}, `;
    }
    address += `USA`;

    // Make the request to the Google Maps Geocoding API
    const { data } = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json`,
      {
        params: {
          address: address,
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        },
      },
    );

    setGoogleResults(data.results);
  };

  const onLocationSelected = async (result: any) => {
    setIsSaving(true);
    try {
      if (mode === "create") {
        const newLocationId = await createLocation({
          name: result.formatted_address,
          formattedAddress: result.formatted_address,
          latitude: result.geometry.location.lat,
          longitude: result.geometry.location.lng,
          placeId: result.place_id,
          caseId,
          caseIds: [caseId],
        });
        if (onLocationCreatedCallback && newLocationId) {
          onLocationCreatedCallback(newLocationId);
        }
      } else if (mode === "edit" && editingLocation) {
        await updateLocation(editingLocation.id, {
          name: result.formatted_address,
          formattedAddress: result.formatted_address,
          latitude: result.geometry.location.lat,
          longitude: result.geometry.location.lng,
          placeId: result.place_id,
        });
        setSelectedCase(editingLocation.caseId);
      }
      handleClose();
    } catch (error) {
      setErrors({ saveError: (error as Error).message });
    } finally {
      setIsSaving(false);
    }
  };

  const onFavoriteLocationSelected = async (location: Location) => {
    setIsSaving(true);
    try {
      if (mode === "create") {
        const newLocationId = await createLocation({
          name: location.name ?? location.formattedAddress,
          formattedAddress: location.formattedAddress,
          latitude: location.latitude,
          longitude: location.longitude,
          placeId: location.placeId,
          caseId,
          caseIds: [caseId],
        });
        if (onLocationCreatedCallback && newLocationId) {
          onLocationCreatedCallback(newLocationId);
        }
      } else if (mode === "edit" && editingLocation) {
        await updateLocation(editingLocation.id, {
          name: location.name ?? location.formattedAddress,
          formattedAddress: location.formattedAddress,
          latitude: location.latitude,
          longitude: location.longitude,
          placeId: location.placeId,
        });
        setSelectedCase(editingLocation.caseId);
      }
      handleClose();
    } catch (error) {
      setErrors({ saveError: (error as Error).message });
    } finally {
      setIsSaving(false);
    }
  };

  const onPlaceSelect = async (
    result: google.maps.places.PlaceResult | null,
  ) => {
    if (!result) return;
    const { data } = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json`,
      {
        params: {
          address: result.formatted_address,
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        },
      },
    );
    if (data.results.length > 1) {
      setGoogleResults(data.results);
    } else {
      await onLocationSelected(data.results[0]);
    }
  };

  const handleSubmit = async () => {
    if (mode === "edit" && editingLocation) {
      setIsSaving(true);
      try {
        await updateLocation(editingLocation.id, { name });
        setSelectedCase(editingLocation.caseId);
        handleClose();
      } catch (error) {
        setErrors({ saveError: (error as Error).message });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const clearForm = () => {
    setStreet("");
    setStreet2("");
    setCity("");
    setState("");
    setZip("");
    setName("");
    setErrors({});
    setGoogleResults([]);
    setVisualState("google");
  };

  const handleClose = () => {
    setIsOpen(false);
    clearForm();
  };

  // deduplicate favorite locations by placeId
  const pastLocationPlaceIds = new Set(
    locations.map((location) => location.placeId),
  );
  const pastLocations = locations.filter((location) =>
    pastLocationPlaceIds.has(location.placeId),
  );

  const getDialogTitle = () => {
    if (mode === "edit") return "Edit Location";
    if (googleResults.length === 0) return "Create Location";
    return "Select Location";
  };

  const tabOptions = [
    { key: "google" as const, label: "Search", icon: MagnifyingGlassIcon },
    { key: "manual" as const, label: "Manual Entry", icon: PencilSquareIcon },
    ...(pastLocations.length > 0
      ? [{ key: "past" as const, label: "Recent", icon: ClockIcon }]
      : []),
  ];

  return (
    <LocationDialogContext.Provider value={{ openCreateDialog, openEditDialog }}>
      {children}
      <Dialog open={isOpen} onClose={handleClose} size="lg">
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogDescription>
          {mode === "edit"
            ? "Update the name for this location."
            : "Add a visit location by searching, entering manually, or selecting from recent locations."}
        </DialogDescription>
        <DialogBody className="space-y-6">
          {errors.saveError && (
            <div className="p-3 border border-red-200 rounded-lg bg-red-50 dark:border-red-800 dark:bg-red-900/20">
              <Text className="text-sm text-red-700 dark:text-red-300">
                {errors.saveError}
              </Text>
            </div>
          )}

          {/* Edit mode - simple name input */}
          {mode === "edit" && (
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Location Name</Label>
                  <Input
                    type="text"
                    defaultValue={editingLocation?.name}
                    placeholder="e.g., Downtown Office, Central Park"
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSaving}
                  />
                </Field>
                {editingLocation?.formattedAddress && (
                  <div className="p-3 border rounded-lg border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
                    <div className="flex items-start gap-2">
                      <MapPinIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-500" />
                      <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                        {editingLocation.formattedAddress}
                      </Text>
                    </div>
                  </div>
                )}
              </FieldGroup>
            </Fieldset>
          )}

          {/* Create mode - full location creation flow */}
          {mode === "create" && (
            <>
              {/* Segmented Control Tabs */}
              <div className="flex p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                {tabOptions.map((tab) => {
                  const isActive = visualState === tab.key;
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setVisualState(tab.key)}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100"
                          : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
                      }`}
                    >
                      <TabIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Google Search Results */}
              {googleResults.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Select a location
                    </Text>
                    <Button plain onClick={() => setGoogleResults([])}>
                      Clear results
                    </Button>
                  </div>
                  <div className="overflow-y-auto border rounded-lg max-h-64 border-zinc-200 dark:border-zinc-700">
                    {googleResults.map((result: any, index: number) => (
                      <button
                        key={result.place_id}
                        type="button"
                        onClick={() => onLocationSelected(result)}
                        disabled={isSaving}
                        className={`flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800 ${
                          index !== 0 ? "border-t border-zinc-200 dark:border-zinc-700" : ""
                        } ${isSaving ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full dark:bg-indigo-900/30">
                          <MapPinIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <Text className="flex-1 text-sm text-zinc-900 dark:text-zinc-100">
                          {result.formatted_address}
                        </Text>
                        <CheckIcon className="w-5 h-5 text-green-600 opacity-0 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Google Search Input */}
              {visualState === "google" && googleResults.length === 0 && (
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label>Search for an address</Label>
                      <PlaceAutocomplete onPlaceSelect={onPlaceSelect} />
                      <Text className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                        Start typing to search for addresses using Google Maps
                      </Text>
                    </Field>
                  </FieldGroup>
                </Fieldset>
              )}

              {/* Past Locations */}
              {visualState === "past" && pastLocations.length > 0 && (
                <div className="space-y-2">
                  <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Recent locations
                  </Text>
                  <div className="overflow-y-auto border rounded-lg max-h-64 border-zinc-200 dark:border-zinc-700">
                    {pastLocations.map((location, index) => (
                      <button
                        key={location.id}
                        type="button"
                        onClick={() => onFavoriteLocationSelected(location)}
                        disabled={isSaving}
                        className={`flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800 ${
                          index !== 0 ? "border-t border-zinc-200 dark:border-zinc-700" : ""
                        } ${isSaving ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
                          <ClockIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Text className="font-medium truncate text-zinc-900 dark:text-zinc-100">
                            {location.name ?? location.formattedAddress}
                          </Text>
                          {location.name && location.formattedAddress && (
                            <Text className="text-xs truncate text-zinc-500 dark:text-zinc-400">
                              {location.formattedAddress}
                            </Text>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Entry Form */}
              {visualState === "manual" && googleResults.length === 0 && (
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label>Street Address</Label>
                      <Input
                        type="text"
                        value={street}
                        placeholder="123 Main Street"
                        onChange={(e) => setStreet(e.target.value)}
                        disabled={isSaving}
                      />
                      {errors.street && (
                        <ErrorMessage>{errors.street}</ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>
                        Apt, Suite, etc.{" "}
                        <span className="text-zinc-400">(optional)</span>
                      </Label>
                      <Input
                        type="text"
                        value={street2}
                        placeholder="Apt 4B"
                        onChange={(e) => setStreet2(e.target.value)}
                        disabled={isSaving}
                      />
                    </Field>
                    <div className="grid grid-cols-6 gap-4">
                      <Field className="col-span-3">
                        <Label>City</Label>
                        <Input
                          type="text"
                          value={city}
                          placeholder="Los Angeles"
                          onChange={(e) => setCity(e.target.value)}
                          disabled={isSaving}
                        />
                        {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                      </Field>
                      <Field className="col-span-1">
                        <Label>State</Label>
                        <Input
                          type="text"
                          value={state}
                          placeholder="CA"
                          maxLength={2}
                          onChange={(e) => setState(e.target.value.toUpperCase())}
                          disabled={isSaving}
                        />
                        {errors.state && <ErrorMessage>{errors.state}</ErrorMessage>}
                      </Field>
                      <Field className="col-span-2">
                        <Label>ZIP Code</Label>
                        <Input
                          type="text"
                          value={zip}
                          placeholder="90001"
                          onChange={(e) => setZip(e.target.value)}
                          disabled={isSaving}
                        />
                        {errors.zip && <ErrorMessage>{errors.zip}</ErrorMessage>}
                      </Field>
                    </div>
                  </FieldGroup>
                </Fieldset>
              )}
            </>
          )}
        </DialogBody>

        <DialogActions>
          <Button plain onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          {mode === "edit" && (
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          )}
          {mode === "create" && visualState === "manual" && googleResults.length === 0 && (
            <Button onClick={validateAddressWithGoogle} disabled={isSaving || !street}>
              {isSaving ? "Validating..." : "Validate & Add"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocationDialogContext.Provider>
  );
}; 